import { Box, Flex, Grid } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { getFromUrl } from '../../api/Api';
import Card from '../../components/Dashboard/Card';
import DefaultCards from '../../components/Dashboard/DefaultCard';
import SymptomsChart from '../../components/Dashboard/SymptomsChart';
import TimeCard from '../../components/Dashboard/TimeCard';
import TotalUnassignedCard from '../../components/Dashboard/TotalUnassignedCard';
import UnassignedSeverityCard from '../../components/Dashboard/UnassignedSeverityCard';
import DashboardSkeleton from '../../components/Skeletons/DashboardSkeleton';
import { AuthContext } from '../../context/AuthContext';
import { CasesOverview, Indexable } from '../../interface';
import DashboardLayout from '../../layouts/dashboard/DashboardLayout';
import { colors } from '../../theme/colors';
import { capitalize } from '../../utils/helper';

const Dashboard = () => {
  interface GraphInterface {
    name: string;
    value: number;
  }

  interface Count {
    counsellors: number;
    clients: number;
    female: number;
    male: number;
  }

  const { signOut } = useContext(AuthContext);
  const [data, setData] = useState([] as GraphInterface[]);
  const [casesOverview, setCasesOverview] = useState({} as CasesOverview);
  const [usersOverview, setUsersOverview] = useState({} as Count);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        // If parsing fails, try to use the string directly
        setUser({ name: cachedUser });
      }
    }

    getUserOverview().catch((err) => console.error(err));
    getCasesOverview().catch((err) => console.error(err));
  }, []);

  const getUserOverview = async () => {
    try {
      const overview = await getFromUrl('users/overview');
      if (overview?.status === 401) signOut();
      if (typeof overview === 'object') {
        setUsersOverview(overview?.count);
      }
    } catch (e: any) {
      return;
    }
  };

  const getCasesOverview = async () => {
    try {
      const overview = await getFromUrl(
        'cases/metrics/comprehensive?range=ALL_TIME'
      );
      if (overview?.status === 401) signOut();
      if (typeof overview === 'object') {
        setCasesOverview(overview);
        createGraphData(overview?.prevalentSymptoms);
      }
    } catch (e: any) {
      return;
    }
  };

  const { total, stages, severity, assignments } = casesOverview || {};

  const createGraphData = (object: any) => {
    const graphData = [];
    for (const key in object) {
      const item = {
        name: key,
        value: (object as Indexable)[key]
      };
      graphData.push(item);
    }
    setData(graphData);
  };
  return (
    <DashboardLayout
      pageTitle={`Welcome ${
        user?.name ? capitalize(user.name.split(' ')[0]) : 'User'
      }`}
    >
      {usersOverview &&
      casesOverview &&
      Object.keys(usersOverview).length &&
      Object.keys(casesOverview).length ? (
        <Grid gridTemplateColumns='1fr 2.4fr' gap='40px' pb='54px'>
          <Box>
            <TotalUnassignedCard no={total?.allCases || 0} />
            <Card h='108px' mb='16px'>
              <DefaultCards
                no={total?.activeCases || 0}
                text='Active Cases'
                color={colors.dark_green}
                icon='Folder'
              />
            </Card>
            <Card h='108px' mb='16px'>
              <DefaultCards
                no={stages?.ACTIVE || 0}
                text='Active Stage Cases'
                color={colors.deep_blue}
                icon='Folder'
              />
            </Card>
            <Card h='108px' mb='16px'>
              <DefaultCards
                no={stages?.ONGOING || 0}
                text='Ongoing Stage Cases'
                color={colors.accent}
                icon='Folder'
              />
            </Card>
          </Box>
          <Box pr='70px'>
            <UnassignedSeverityCard severity={severity} />
            <Card h='108px' mb='16px'>
              <DefaultCards
                no={assignments?.unassigned || 0}
                text='Unassigned Cases'
                color={colors.warning}
                icon='Folder'
              />
            </Card>
            <Grid gridTemplateColumns='1fr 1fr' gap='24px' mb='16px'>
              <Card h='80px' px='24px'>
                <DefaultCards
                  no={usersOverview.counsellors}
                  text='Total Counsellors'
                  color={colors.deep_blue}
                  icon='Profile'
                  bold
                />
              </Card>
              <Card h='80px' px='24px'>
                <DefaultCards
                  no={usersOverview.clients}
                  text='Total Clients'
                  color={colors.default_green}
                  icon='Profile'
                  bold
                />
              </Card>
            </Grid>
            <Flex mb='16px'>
              <TimeCard
                no={
                  typeof casesOverview?.averageWaitTimeInHours === 'number'
                    ? casesOverview?.averageWaitTimeInHours
                    : 0
                }
              />
              <Box w='24px' />
              <SymptomsChart data={data} />
            </Flex>
          </Box>
        </Grid>
      ) : (
        <DashboardSkeleton />
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
